<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import { AppState, SetNotification, ToastQueue } from '../../../etc/stores';
  import { formatMoney } from '../../../etc/utils';
  import { Auth } from '../../../services/auth';
  import { Container } from '../../../services/container';
  import { Logger } from '../../../services/logger';
  import { SessionManager } from '../../../services/session-manager';
  import Analytics from '../../../services/analytics';
  import Button from '../../form/Button.svelte';
  import Text from '../../misc/Text.svelte';
  import Title from '../../misc/Title.svelte';
  import Link from '../../misc/Link.svelte';
  import Close from '../../icons/Close.svelte';
  import { Customer } from '../../../services/customer';
  import AnimatedCoin from '../../misc/AnimatedCoin.svelte';
  import { replaceVariables } from '../../../i18n';
  import BackgroundProvisionalCoin from '../../icons/BackgroundProvisionalCoin.svelte';
  import { Checkout } from '../../../services/checkout';
  import { extractMessageFromError } from '../../../services/api';
  import sessionKeys from '../../../etc/sessionKeys';

  const auth = Container.get(Auth);
  const logger = Container.get(Logger).context(
    'ProvisionalCoins.svelte'
  ) as Logger;
  const session = Container.get(SessionManager) as SessionManager;
  const customer = Container.get(Customer) as Customer;
  const checkout = Container.get(Checkout) as Checkout;

  let loading = false;
  let success = false;
  let error = false;

  $: showLoginButton = $AppState.auth?.providerData || auth.canOpenLoginModal();

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`messages.provisional_coins.${text}`), values);
  };

  const handleConnectCustomer = async () => {
    try {
      if (loading || success) return;
      loading = true;
      error = false;

      const next = await auth.connectAccount();
      onGAEventGetCoins();
      session.set(sessionKeys.COLLECT_COINS, true);
      success = true;

      await checkout.verifyProvisionalCheckoutWix();

      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          popoverOpen: true,
          popoverRouteOnOpen: next
        }
      }));
      closeMessage();
    } catch (e) {
      error = true;
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loading = false;
    }
  };

  const onGAEventClickGetCoins = event => {
    Analytics.logEvent(
      event,
      `provisional-${Analytics.getCategoryLoginState()}`
    );
  };

  const onGAEventGetCoins = () => {
    if (success) return;

    Analytics.logEvent(
      'click-collect-coins-success',
      `provisional-${Analytics.getCategoryLoginState()}`
    );
  };

  async function handleSubmit(signIn?: boolean) {
    if ($AppState.auth.connected) {
      loading = true;
      await customer.loadState();
      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          popoverOpen: true,
          checkout: false,
          lightIcon: false,
          connectingAccount: false,
          popoverRouteOnOpen: {
            route: '/dashboard'
          }
        }
      }));
      closeMessage();
      return;
    }
    if (await auth.isAuthenticatedOnWebsite()) {
      onGAEventClickGetCoins('click-collect-coins-logged');
      await handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      onGAEventClickGetCoins('click-collect-coins-guest');
      auth.openLoginModal({ mode: signIn ? 'login' : 'signup' });
    }
  }

  const onSignIn = () => {
    handleSubmit(true);
  };

  $: if ($AppState.website.connectingAccount && !error) {
    handleConnectCustomer();
  }

  const closeMessage = () => {
    SetNotification('');
  };

  onMount(() => {
    sessionStorage.removeItem('@twism:purchase');
  });
</script>

<div class="twism-provisional-coins--container {auth.provider}">
  <div class="twism-provisional-coins--header">
    <div class="twism-provisional-coins--close" on:click={closeMessage}>
      <Close />
    </div>
    <div class="twism-provisional-coins--background">
      <BackgroundProvisionalCoin />
    </div>

    <div class="twism-provisional-coins--coin">
      <AnimatedCoin image={$AppState.business.logo.publicPath.large} />
    </div>
  </div>
  <div class="twism-provisional-coins--wrapper">
    <div class="twism-provisional-coins--description">
      <Text
        fontSize="16px"
        lineHeight="24px"
        color="#012A5A"
        letterSpacing="-0.005em"
      >
        {t('description_subtext')}
      </Text>
      <Title
        fontSize="24px"
        lineHeight="32px"
        letterSpacing="-0.005em"
        color="#012A5A"
      >
        {@html t('description', {
          AMOUNT: formatMoney(
            $AppState.business.location,
            $AppState.website.coins || 0
          ),
          BUSINESS: $AppState.business.name
        })}
      </Title>
    </div>
    {#if !$AppState.auth.connected}
      <div class="twism-provisional-coins--explanation">
        <Text
          fontSize="16px"
          lineHeight="22px"
          color="#597492"
          letterSpacing="-0.005em"
        >
          {t('explanation')}
        </Text>
      </div>
    {/if}
    {#if showLoginButton}
      <div class="twism-provisional-coins--button">
        <Button label={t('btn')} on:click={() => handleSubmit()} {loading} />
      </div>
      <div class="twism-provisional-coins--secondary-button">
        <Link fontSize="15px" lineHeight="20px" on:click={onSignIn}
          >{t('second-btn')}</Link
        >
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @use './styles.scss' as *;

  .twism-provisional-coins {
    &--container {
      background-color: #ffffff;
      background-size: 100% 350px;
      background-repeat: no-repeat;
      box-shadow: 0px 16.8309px 57.7059px -15.6287px rgba(4, 33, 67, 0.26);
      border-radius: 20px;
      width: 360px;
      height: auto;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      box-sizing: border-box;
      padding-bottom: 50px;
      transform-origin: inherit;
      position: relative;
      overflow: hidden;

      @media screen and (max-width: 468px) {
        max-height: calc(100vh - 110px);
        width: 100%;
      }
    }

    &--background {
      position: absolute;
      top: 0;
    }

    &--wrapper {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 24px;
      width: calc(100% - 48px);
    }

    &--close {
      position: absolute;
      top: 24px;
      right: 24px;
      width: 30px;
      height: 30px;
      cursor: pointer;
      border-radius: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: 1px solid var(--twism-theme-illustration-quinary);
      transition: background 0.2s ease-in-out;
      z-index: 5;

      :global(svg path) {
        fill: var(--twism-theme-illustration-quinary);
      }

      @media screen and (min-width: 768px) {
        &:hover {
          background: rgba(0, 0, 0, 0.04);
        }
      }
    }

    &--header {
      height: 140px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      background-position: top;
      background-repeat: no-repeat;
      background-size: 100% 95px;
      margin-bottom: 24px;

      @media screen and (max-width: 468px) {
        margin-bottom: 16px;
      }
    }

    &--coin {
      width: 96px;
      height: 96px;
      display: flex;
      justify-content: center;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      padding-bottom: 6px;
      padding-left: 2px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;

      @media screen and (max-width: 468px) {
        width: 88px;
        height: 88px;
      }

      &--logo {
        border-radius: 50%;
        overflow: hidden;
        width: 72px;
        height: 72px;

        @media screen and (max-width: 468px) {
          width: 66px;
          height: 66px;
          transform: translate(-0.5px);
        }
      }
    }

    &--description {
      margin-bottom: 32px;

      :global(h1 > span) {
        color: var(--twism-theme-link);
        display: contents;
      }

      :global(h1) {
        margin-top: 4px !important;
      }

      @media screen and (max-width: 468px) {
        margin-bottom: 20px;
      }
    }

    &--explanation {
      margin-bottom: 24px;

      @media screen and (max-width: 468px) {
        :global(p) {
          font-size: 14px !important;
          line-height: 20px !important;
        }
      }
    }

    &--button {
      width: 100%;
      position: relative;

      span {
        font-size: 40px;
        position: absolute;
        bottom: 24px;
        left: 16px;
        z-index: 10;
        pointer-events: none;

        @media screen and (max-width: 468px) {
          font-size: 36px;
        }
      }
    }

    &--bottom {
      position: absolute;
      bottom: 14px;
    }

    &--secondary-button {
      margin-top: 22px;
    }
  }
</style>
